/**
 * Authentication Context Provider
 *
 * Manages authentication state throughout the application including:
 * - User session state
 * - Login/logout operations
 * - Session restoration on page load
 * - CSRF token management
 */

import React, {
  createContext,
  useContext,
  useState,
  useEffect,
  useCallback,
  useMemo,
  useRef,
} from 'react';
import { useNavigate } from 'react-router-dom';
import type { AuthUser, AuthUserResponse, Role } from '../../../shared/types';
import { authApi, clearCsrfToken, AuthApiError } from '../services/authApi';
import { setUnauthorizedHandler, clearUnauthorizedHandler } from '../services/api';
import { deriveRoleFromPermissions } from '../utils/permissions';

/** Map API response (permissions-based) to internal AuthUser (role-based) */
function toAuthUser(resp: AuthUserResponse): AuthUser {
  return {
    id: resp.id,
    email: resp.email,
    role: deriveRoleFromPermissions(resp.permissions),
    companyId: resp.companyId,
    mustChangePassword: resp.mustChangePassword,
    status: resp.status,
  };
}

/**
 * Auth context state and methods
 */
export interface AuthContextType {
  /** Current authenticated user, null if not logged in */
  user: AuthUser | null;
  /** Whether the user is authenticated */
  isAuthenticated: boolean;
  /** Whether the initial session check is in progress */
  isLoading: boolean;
  /** Login with email and password */
  login: (email: string, password: string) => Promise<LoginResult>;
  /** Logout current user */
  logout: () => Promise<void>;
  /** Refresh user data from server */
  refreshUser: () => Promise<void>;
  /** Clear auth state (used by 401 interceptor) */
  clearAuth: () => void;
}

export interface LoginResult {
  success: boolean;
  user?: AuthUser;
  error?: string;
  code?: string;
  requiresTwoFactor?: boolean;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

/**
 * Get the dashboard path based on user role
 */
export function getDashboardPath(role: Role): string {
  switch (role) {
    case 'SUPER_ADMIN':
    case 'ADMIN':
      return '/admin';
    case 'COMPANY_MANAGER':
      return '/manager';
    case 'COMPANY_USER':
    default:
      return '/dashboard';
  }
}

interface AuthProviderProps {
  children: React.ReactNode;
}

export const AuthProvider: React.FC<AuthProviderProps> = ({ children }) => {
  const [user, setUser] = useState<AuthUser | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const navigate = useNavigate();

  // Use ref to track if we've already redirected to login (avoid duplicate redirects)
  const hasRedirectedRef = useRef(false);

  /**
   * Clear authentication state
   * Used when session expires or user logs out
   */
  const clearAuth = useCallback(() => {
    setUser(null);
    clearCsrfToken();
  }, []);

  /**
   * Handle 401 unauthorized responses from API
   * Clears auth state and redirects to login
   */
  const handleUnauthorized = useCallback(() => {
    // Prevent multiple redirects
    if (hasRedirectedRef.current) return;
    hasRedirectedRef.current = true;

    clearAuth();
    navigate('/login', { replace: true });

    // Reset the flag after a short delay to allow future redirects
    setTimeout(() => {
      hasRedirectedRef.current = false;
    }, 1000);
  }, [clearAuth, navigate]);

  /**
   * Register 401 handler with API service on mount
   */
  useEffect(() => {
    setUnauthorizedHandler(handleUnauthorized);

    return () => {
      clearUnauthorizedHandler();
    };
  }, [handleUnauthorized]);

  /**
   * Restore session on mount by checking /api/auth/me
   */
  useEffect(() => {
    let mounted = true;

    async function restoreSession() {
      try {
        const response = await authApi.getMe();

        if (!mounted) return;

        if (response.success && response.user) {
          setUser(toAuthUser(response.user));
          // Read CSRF token from cookie for authenticated session
          authApi.fetchCsrfToken();
        } else {
          setUser(null);
        }
      } catch (error) {
        if (!mounted) return;

        // Session doesn't exist or expired - this is normal for logged out users
        setUser(null);
      } finally {
        if (mounted) {
          setIsLoading(false);
        }
      }
    }

    restoreSession();

    return () => {
      mounted = false;
    };
  }, []);

  /**
   * Login with email and password
   */
  const login = useCallback(async (email: string, password: string): Promise<LoginResult> => {
    try {
      const response = await authApi.login(email, password);

      if (response.success && response.user) {
        const authUser = toAuthUser(response.user);
        setUser(authUser);

        // Read CSRF token from cookie after successful login
        authApi.fetchCsrfToken();

        return { success: true, user: authUser };
      }

      if (response.requiresTwoFactor) {
        return {
          success: false,
          requiresTwoFactor: true,
          error: 'Two-factor authentication required',
        };
      }

      return { success: false, error: 'Login failed' };
    } catch (error) {
      const message =
        error instanceof AuthApiError
          ? error.message
          : 'An error occurred during login';

      const code =
        error instanceof AuthApiError
          ? error.code
          : undefined;

      return { success: false, error: message, code };
    }
  }, []);

  /**
   * Logout current user
   */
  const logout = useCallback(async () => {
    try {
      await authApi.logout();
    } catch (error) {
      // Even if server logout fails, clear local state
      console.error('Logout error:', error);
    } finally {
      clearAuth();
      navigate('/login');
    }
  }, [clearAuth, navigate]);

  /**
   * Refresh user data from server
   */
  const refreshUser = useCallback(async () => {
    try {
      const response = await authApi.getMe();

      if (response.success && response.user) {
        setUser(toAuthUser(response.user));
      } else {
        clearAuth();
      }
    } catch (error) {
      clearAuth();
    }
  }, [clearAuth]);

  const isAuthenticated = user !== null;

  const value = useMemo<AuthContextType>(
    () => ({
      user,
      isAuthenticated,
      isLoading,
      login,
      logout,
      refreshUser,
      clearAuth,
    }),
    [user, isAuthenticated, isLoading, login, logout, refreshUser, clearAuth]
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

/**
 * Hook to access auth context
 * Must be used within an AuthProvider
 */
export function useAuth(): AuthContextType {
  const context = useContext(AuthContext);

  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }

  return context;
}

export default AuthContext;
